import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../context/AuthContext";
import { ChevronDown, ChevronRight, TrendingUp, DollarSign, Clock, CheckCircle2, AlertTriangle } from "lucide-react";

const statusLabels = {
  completed: "Completed",
  in_progress: "In Progress",
  awaiting_payment: "Awaiting Payment",
  awaiting_client: "Awaiting Client",
  contract_signed: "Contract Signed",
  proposal_sent: "Proposal",
};

const statusColors = {
  completed: "bg-green-900/30 text-green-400 border-green-800",
  in_progress: "bg-blue-900/30 text-blue-400 border-blue-800",
  awaiting_payment: "bg-yellow-900/30 text-yellow-400 border-yellow-800",
  awaiting_client: "bg-orange-900/30 text-orange-400 border-orange-800",
  contract_signed: "bg-purple-900/30 text-purple-400 border-purple-800",
  proposal_sent: "bg-zinc-800/50 text-zinc-400 border-zinc-700",
};

const PMBonusesPage = () => {
  const { user } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accruing, setAccruing] = useState(false);
  const [expandedPMs, setExpandedPMs] = useState({});

  // Only privileged roles may trigger accrual (writes payroll rows). A plain PM
  // viewing their own bonuses must never book payroll from a page load.
  const canAccrue = ["admin", "pm_lead", "finance", "crm_manager"].includes(user?.role);
  const autoRan = useRef(false);

  const load = useCallback(async () => {
    const res = await api.get("/pm-bonuses-overview");
    const rows = res.data || [];
    setData(rows);
    // Auto-expand all PMs
    const expanded = {};
    rows.forEach(pm => { expanded[pm.pm_id] = true; });
    setExpandedPMs(expanded);
    return rows;
  }, []);

  const accrueMissed = useCallback(async () => {
    setAccruing(true);
    try {
      await api.post("/pm-bonuses/accrue-missed");
      await load();
    } catch (err) {
      console.error("Failed to accrue missed PM bonuses:", err);
    } finally {
      setAccruing(false);
    }
  }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const rows = await load();
        // Self-heal: on first load, auto-accrue any "missed" bonuses so the bucket
        // empties without a manual step. Gated to privileged roles + runs once.
        const missedCount = rows.reduce(
          (n, pm) => n + (pm.potential || []).filter(p => p.type === "missed").length, 0);
        if (canAccrue && missedCount > 0 && !autoRan.current) {
          autoRan.current = true;
          await accrueMissed();
        }
      } catch (err) {
        console.error("Failed to load PM bonuses:", err);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const togglePM = (pmId) => {
    setExpandedPMs(prev => ({ ...prev, [pmId]: !prev[pmId] }));
  };

  const totals = useMemo(() => {
    let accrued = 0, potential = 0, missed = 0;
    data.forEach(pm => {
      accrued += pm.total_accrued_usd || 0;
      potential += pm.total_potential_usd || 0;
      missed += (pm.potential || []).filter(p => p.type === "missed").reduce((s, p) => s + p.bonus_usd, 0);
    });
    return { accrued, potential, missed };
  }, [data]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="pm-bonuses-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-zinc-100">PM Bonuses</h1>
        {canAccrue && totals.missed > 0 && (
          <button
            onClick={accrueMissed}
            disabled={accruing}
            data-testid="accrue-missed-btn"
            className="inline-flex items-center gap-2 rounded-md border border-red-800 bg-red-900/30 px-3 py-1.5 text-sm font-medium text-red-300 hover:bg-red-900/50 disabled:opacity-50 transition-colors"
          >
            <AlertTriangle className="h-4 w-4" />
            {accruing ? "Accruing…" : `Accrue missed ($${totals.missed.toFixed(2)})`}
          </button>
        )}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-900/30">
                <CheckCircle2 className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Accrued</p>
                <p className="text-xl font-bold text-green-400" data-testid="total-accrued">${totals.accrued.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-900/30">
                <TrendingUp className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Potential</p>
                <p className="text-xl font-bold text-blue-400" data-testid="total-potential">${totals.potential.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-900/30">
                <AlertTriangle className="h-5 w-5 text-red-400" />
              </div>
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Missed (need accrual)</p>
                <p className="text-xl font-bold text-red-400" data-testid="total-missed">${totals.missed.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* PM sections */}
      {data.map(pm => (
        <Card key={pm.pm_id} className="bg-zinc-900 border-zinc-800" data-testid={`pm-section-${pm.pm_id}`}>
          <CardHeader
            className="cursor-pointer hover:bg-zinc-800/50 transition-colors py-4"
            onClick={() => togglePM(pm.pm_id)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {expandedPMs[pm.pm_id] ? <ChevronDown className="h-4 w-4 text-zinc-500" /> : <ChevronRight className="h-4 w-4 text-zinc-500" />}
                <CardTitle className="text-base text-zinc-200">
                  {pm.pm_name}
                </CardTitle>
                <span className="text-xs text-zinc-600">({pm.bonus_pct}% of profit)</span>
              </div>
              <div className="flex items-center gap-6 text-sm">
                <span className="text-green-400">
                  <DollarSign className="h-3.5 w-3.5 inline" />{pm.total_accrued_usd.toFixed(2)}
                </span>
                <span className="text-blue-400">
                  <Clock className="h-3.5 w-3.5 inline mr-1" />${pm.total_potential_usd.toFixed(2)}
                </span>
                <span className="text-zinc-500">
                  {pm.accrued.length + pm.potential.length} projects
                </span>
              </div>
            </div>
          </CardHeader>
          {expandedPMs[pm.pm_id] && (
            <CardContent className="pt-0 pb-4">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-zinc-500 text-xs">Project</TableHead>
                    <TableHead className="text-zinc-500 text-xs text-center">Status</TableHead>
                    <TableHead className="text-zinc-500 text-xs text-right">Revenue</TableHead>
                    <TableHead className="text-zinc-500 text-xs text-right">Costs</TableHead>
                    <TableHead className="text-zinc-500 text-xs text-right">Profit</TableHead>
                    <TableHead className="text-zinc-500 text-xs text-center">Paid %</TableHead>
                    <TableHead className="text-zinc-500 text-xs text-right">Bonus</TableHead>
                    <TableHead className="text-zinc-500 text-xs text-center">Type</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* Accrued bonuses first */}
                  {pm.accrued.map(p => (
                    <TableRow key={p.project_id} className="border-zinc-800/50 hover:bg-zinc-800/30">
                      <TableCell>
                        <a href={`/sales/project/${p.project_id}`} className="text-zinc-200 hover:text-orange-400 transition-colors text-sm">
                          {p.project_name}
                        </a>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={`text-[10px] ${statusColors[p.status] || "bg-zinc-800 text-zinc-400 border-zinc-700"}`}>
                          {statusLabels[p.status] || p.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right text-sm text-zinc-400">${p.revenue_usd.toFixed(0)}</TableCell>
                      <TableCell className="text-right text-sm text-zinc-400">${p.costs_usd.toFixed(0)}</TableCell>
                      <TableCell className="text-right text-sm text-zinc-300">${p.profit_usd.toFixed(0)}</TableCell>
                      <TableCell className="text-center">
                        <span className="text-xs text-green-400">{p.payment_progress}%</span>
                      </TableCell>
                      <TableCell className="text-right font-medium text-green-400 text-sm">${p.accrued_amount}</TableCell>
                      <TableCell className="text-center">
                        <Badge className="bg-green-900/30 text-green-400 border-green-800 text-[10px]">
                          {p.accrued_status === "paid" ? "Paid" : "Accrued"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Potential/Missed bonuses */}
                  {pm.potential.map(p => (
                    <TableRow
                      key={p.project_id}
                      className={`border-zinc-800/50 ${p.type === "missed" ? "bg-red-950/20 hover:bg-red-950/30" : "hover:bg-zinc-800/30"}`}
                    >
                      <TableCell>
                        <a href={`/sales/project/${p.project_id}`} className="text-zinc-300 hover:text-orange-400 transition-colors text-sm">
                          {p.project_name}
                        </a>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={`text-[10px] ${statusColors[p.status] || "bg-zinc-800 text-zinc-400 border-zinc-700"}`}>
                          {statusLabels[p.status] || p.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right text-sm text-zinc-500">${p.revenue_usd.toFixed(0)}</TableCell>
                      <TableCell className="text-right text-sm text-zinc-500">${p.costs_usd.toFixed(0)}</TableCell>
                      <TableCell className="text-right text-sm text-zinc-400">${p.profit_usd.toFixed(0)}</TableCell>
                      <TableCell className="text-center">
                        <ProgressBar value={p.payment_progress} />
                      </TableCell>
                      <TableCell className="text-right font-medium text-sm text-zinc-400">${p.bonus_usd.toFixed(2)}</TableCell>
                      <TableCell className="text-center">
                        {p.type === "missed" ? (
                          <Badge className="bg-red-900/30 text-red-400 border-red-800 text-[10px]">Missed</Badge>
                        ) : (
                          <Badge className="bg-zinc-800/50 text-zinc-500 border-zinc-700 text-[10px]">Potential</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {pm.accrued.length === 0 && pm.potential.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-zinc-600 text-sm py-4">No projects</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          )}
        </Card>
      ))}
    </div>
  );
};

const ProgressBar = ({ value }) => (
  <div className="flex items-center gap-2 justify-center">
    <div className="w-16 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all ${value >= 100 ? "bg-green-500" : value >= 50 ? "bg-blue-500" : "bg-zinc-600"}`}
        style={{ width: `${Math.min(value, 100)}%` }}
      />
    </div>
    <span className={`text-[10px] ${value >= 100 ? "text-green-400" : "text-zinc-500"}`}>{value}%</span>
  </div>
);

export default PMBonusesPage;
